import { Suspense, lazy, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import RootLayout from '@/layouts/RootLayout';
import DashboardLayout from '@/layouts/DashboardLayout';
import AdminLayout from '@/layouts/AdminLayout';
import ProfileLayout from '@/layouts/ProfileLayout';
import RequireAuth from '@/components/auth/RequireAuth';
import RequireRole, { ADMIN_ROLES, WAREHOUSE_ROLES } from '@/components/auth/RequireRole';
import AppShell from '@/layouts/AppShell';

// Each page is a separate dynamic chunk. Combined with Vite's default
// route-level code splitting this drops initial bundle to <100 KB and
// loads each page on demand.
const Home = lazy(() => import('@/pages/index'));
const Login = lazy(() => import('@/pages/login/index'));
const Debug = lazy(() => import('@/pages/debug/index'));

const Dashboard = lazy(() => import('@/pages/dashboard/index'));
const DashboardBarcodes = lazy(() => import('@/pages/dashboard/barcodes/index'));
const DashboardBundles = lazy(() => import('@/pages/dashboard/bundles/index'));
const DashboardBundleDetail = lazy(() => import('@/pages/dashboard/bundles/[bundleId]/index'));
const DashboardCompliance = lazy(() => import('@/pages/dashboard/compliance/index'));
const DashboardContract = lazy(() => import('@/pages/dashboard/contract/index'));
const DashboardDamageReports = lazy(() => import('@/pages/dashboard/damage-reports/index'));
const DashboardEmergencyDelivery = lazy(() => import('@/pages/dashboard/emergency-delivery/index'));
const DashboardHistory = lazy(() => import('@/pages/dashboard/history/index'));
const DashboardInvoices = lazy(() => import('@/pages/dashboard/invoices/index'));
const DashboardInvoicesAnalytics = lazy(() => import('@/pages/dashboard/invoices/analytics/index'));
const DashboardInvoicesArchive = lazy(() => import('@/pages/dashboard/invoices/archive/index'));
const DashboardIssues = lazy(() => import('@/pages/dashboard/issues/index'));
const DashboardItemDetail = lazy(() => import('@/pages/dashboard/items/[itemId]/index'));
const DashboardNotifications = lazy(() => import('@/pages/dashboard/notifications/index'));
const DashboardNotificationsHistory = lazy(() => import('@/pages/dashboard/notifications/history/index'));
const DashboardReceipts = lazy(() => import('@/pages/dashboard/receipts/index'));
const DashboardReports = lazy(() => import('@/pages/dashboard/reports/index'));
const DashboardSchedule = lazy(() => import('@/pages/dashboard/schedule/index'));
const DashboardUsers = lazy(() => import('@/pages/dashboard/users/index'));

const Admin = lazy(() => import('@/pages/admin/index'));
const AdminBilling = lazy(() => import('@/pages/admin/billing/index'));
const AdminComplianceBreaches = lazy(() => import('@/pages/admin/compliance/breaches/index'));
const AdminComplianceNdpc = lazy(() => import('@/pages/admin/compliance/ndpc/index'));
const AdminDeliveriesReminders = lazy(() => import('@/pages/admin/deliveries/reminders/index'));
const AdminEmergencyDelivery = lazy(() => import('@/pages/admin/emergency-delivery/index'));
const AdminFleet = lazy(() => import('@/pages/admin/fleet/index'));
const AdminNotificationsDisruption = lazy(() => import('@/pages/admin/notifications/disruption/index'));
const AdminTenants = lazy(() => import('@/pages/admin/tenants/index'));
const AdminTenantContract = lazy(() => import('@/pages/admin/tenants/[id]/contract/index'));
const AdminTenantSchedule = lazy(() => import('@/pages/admin/tenants/[id]/schedule/index'));
const AdminUsers = lazy(() => import('@/pages/admin/users/index'));

const Profile = lazy(() => import('@/pages/profile/index'));
const ProfileNotifications = lazy(() => import('@/pages/profile/notifications/index'));
const ProfilePrivacy = lazy(() => import('@/pages/profile/privacy/index'));
const ProfileSecurity = lazy(() => import('@/pages/profile/security/index'));

const Warehouse = lazy(() => import('@/pages/warehouse/index'));

const PageFallback = (
  <div className="min-h-screen flex items-center justify-center text-sage-700">Loading…</div>
);

// Wraps a route element in Suspense so its lazy chunk gets a fallback while
// downloading. Applied per-leaf so a slow chunk doesn't blank the layout.
function L(node: ReactNode) {
  return <Suspense fallback={PageFallback}>{node}</Suspense>;
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Public
      { path: '/', element: L(<Home />) },
      { path: '/login', element: L(<Login />) },
      { path: '/debug', element: L(<Debug />) },

      // Dashboard — any authenticated customer
      {
        path: '/dashboard',
        element: (
          <RequireAuth>
            <DashboardLayout />
          </RequireAuth>
        ),
        children: [
          { index: true, element: L(<Dashboard />) },
          { path: 'barcodes', element: L(<DashboardBarcodes />) },
          {
            path: 'bundles',
            children: [
              { index: true, element: L(<DashboardBundles />) },
              { path: ':bundleId', element: L(<DashboardBundleDetail />) },
            ],
          },
          { path: 'compliance', element: L(<DashboardCompliance />) },
          { path: 'contract', element: L(<DashboardContract />) },
          { path: 'damage-reports', element: L(<DashboardDamageReports />) },
          { path: 'emergency-delivery', element: L(<DashboardEmergencyDelivery />) },
          { path: 'history', element: L(<DashboardHistory />) },
          {
            path: 'invoices',
            children: [
              { index: true, element: L(<DashboardInvoices />) },
              { path: 'analytics', element: L(<DashboardInvoicesAnalytics />) },
              { path: 'archive', element: L(<DashboardInvoicesArchive />) },
            ],
          },
          { path: 'issues', element: L(<DashboardIssues />) },
          {
            path: 'items',
            children: [{ path: ':itemId', element: L(<DashboardItemDetail />) }],
          },
          {
            path: 'notifications',
            children: [
              { index: true, element: L(<DashboardNotifications />) },
              { path: 'history', element: L(<DashboardNotificationsHistory />) },
            ],
          },
          { path: 'receipts', element: L(<DashboardReceipts />) },
          { path: 'reports', element: L(<DashboardReports />) },
          { path: 'schedule', element: L(<DashboardSchedule />) },
          { path: 'users', element: L(<DashboardUsers />) },
        ],
      },

      // Admin — operations_manager + system_admin only
      {
        path: '/admin',
        element: (
          <RequireRole allow={ADMIN_ROLES}>
            <AdminLayout />
          </RequireRole>
        ),
        children: [
          { index: true, element: L(<Admin />) },
          { path: 'billing', element: L(<AdminBilling />) },
          {
            path: 'compliance',
            children: [
              { path: 'breaches', element: L(<AdminComplianceBreaches />) },
              { path: 'ndpc', element: L(<AdminComplianceNdpc />) },
            ],
          },
          {
            path: 'deliveries',
            children: [{ path: 'reminders', element: L(<AdminDeliveriesReminders />) }],
          },
          { path: 'emergency-delivery', element: L(<AdminEmergencyDelivery />) },
          { path: 'fleet', element: L(<AdminFleet />) },
          {
            path: 'notifications',
            children: [{ path: 'disruption', element: L(<AdminNotificationsDisruption />) }],
          },
          {
            path: 'tenants',
            children: [
              { index: true, element: L(<AdminTenants />) },
              {
                path: ':id',
                children: [
                  { path: 'contract', element: L(<AdminTenantContract />) },
                  { path: 'schedule', element: L(<AdminTenantSchedule />) },
                ],
              },
            ],
          },
          { path: 'users', element: L(<AdminUsers />) },
        ],
      },

      // Profile — any authenticated user
      {
        path: '/profile',
        element: (
          <RequireAuth>
            <ProfileLayout />
          </RequireAuth>
        ),
        children: [
          { index: true, element: L(<Profile />) },
          { path: 'notifications', element: L(<ProfileNotifications />) },
          { path: 'privacy', element: L(<ProfilePrivacy />) },
          { path: 'security', element: L(<ProfileSecurity />) },
        ],
      },

      // Warehouse — warehouse_operations only
      {
        path: '/warehouse',
        element: (
          <RequireRole allow={WAREHOUSE_ROLES}>
            <AppShell />
          </RequireRole>
        ),
        children: [{ index: true, element: L(<Warehouse />) }],
      },

      // 404 → home (Phase 6 swaps for a real NotFound page)
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
